import { useCallback, useMemo } from 'react';
import PortalIdParser from 'PortalIdParser';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { Link } from 'foundations-components/transitional/components/Link';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
// @ts-expect-error untyped
import localeComparator from 'I18n/utils/localeComparator';
import { getCombinedAssociationTypeId } from 'customer-data-objects/associations/AssociationCategoryTypes';
import { useGetHasScope } from '../../v2/hooks/useGetHasScope';
import { jsx as _jsx } from "react/jsx-runtime";
const getAssociationLabelInputTooltip = ({
  hasSpecificLabelRequired,
  isUnsupportedForLabels,
  label
}) => {
  if (hasSpecificLabelRequired && label) {
    return unescapedText('customerDataProperties.AssociationLabelInput.specificLabelRequired');
  } else if (isUnsupportedForLabels) {
    return unescapedText('customerDataProperties.AssociationLabelInput.unsupportedForLabels');
  } else {
    return null;
  }
};
function getCreateNewLabelLink({
  hasCustomAssociationLabelsScope,
  hasCustomAssociationLabelsWriteScope,
  fromObjectTypeId,
  toObjectTypeId
}) {
  const showUpgradeLink = !hasCustomAssociationLabelsScope;
  const showCreateLabelLink = hasCustomAssociationLabelsScope && hasCustomAssociationLabelsWriteScope;
  if (showCreateLabelLink) {
    return /*#__PURE__*/_jsx(Link, {
      external: true,
      href: `/data-model/${PortalIdParser.get()}?tab=associations&fromObjectTypeId=${fromObjectTypeId}&toObjectTypeId=${toObjectTypeId}&associationModal=createAssociationLabel&scrollToObjectId=${toObjectTypeId}`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.AssociationLabelInput.createNewLabel"
      })
    });
  } else if (showUpgradeLink) {
    return /*#__PURE__*/_jsx(Link, {
      external: true,
      href: "https://www.hubspot.com/pricing/sales?products=sales-hub-starter_1&term=annual",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.AssociationLabelInput.upgradeLink"
      })
    });
  } else {
    return null;
  }
}
export const AssociationLabelInput = ({
  associationLabelDefinition,
  associationDefinitions,
  value = null,
  recordIds,
  onChange,
  error = false,
  validationMessage,
  disabled = false,
  objectTypeId,
  placeholder = unescapedText('customerDataProperties.AssociationLabelInput.placeholder'),
  isLabelRequired,
  id
}) => {
  const getHasScope = useGetHasScope();
  const hasCustomAssociationLabelsScope = getHasScope('custom-association-labels');
  const hasCustomAssociationLabelsWriteScope = getHasScope('custom-association-labels-write');
  // requirementId should also be the same as the associationIdentifier
  const defaultAssociationDefinition = associationDefinitions.find(associationDefinition => associationDefinition.isDefault);
  const toObjectTypeId = defaultAssociationDefinition.toObjectTypeId;
  const filterSelectedLabels = useCallback(option => {
    return option.value === value || option.value && true;
  }, [value]);
  const singleLabelOptions = useMemo(() => associationDefinitions.reduce((acc, type) => {
    if ((type.label || type.inverseLabel) && type.label === type.inverseLabel) {
      acc.push({
        text: type.label,
        buttonText: type.label,
        dropdownText: type.label,
        value: getCombinedAssociationTypeId(type)
      });
    }
    return acc;
  }, []).sort((a, b) => localeComparator(a === null || a === void 0 ? void 0 : a.text, b === null || b === void 0 ? void 0 : b.text)).filter(filterSelectedLabels), [associationDefinitions, filterSelectedLabels]);
  const pairedLabelOptions = useMemo(() => associationDefinitions.reduce((acc, type) => {
    if ((type.label || type.inverseLabel) && type.label !== type.inverseLabel) {
      acc.push({
        text: type.label,
        buttonText: type.label,
        dropdownText: type.label,
        help: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "customerDataProperties.AssociationLabelInput.asymmetricLabel",
          options: {
            inverseLabel: type.inverseLabel
          }
        }),
        value: getCombinedAssociationTypeId(type)
      });
    }
    return acc;
  }, []).sort((a, b) => localeComparator(a === null || a === void 0 ? void 0 : a.text, b === null || b === void 0 ? void 0 : b.text)).filter(filterSelectedLabels), [associationDefinitions, filterSelectedLabels]);
  const labelOptions = useMemo(() => [{
    text: unescapedText('customerDataProperties.AssociationLabelInput.singleLabels'),
    options: singleLabelOptions
  }, {
    text: unescapedText('customerDataProperties.AssociationLabelInput.pairedLabels'),
    options: pairedLabelOptions
  }], [singleLabelOptions, pairedLabelOptions]);
  const isUnsupportedForLabels = !defaultAssociationDefinition.allowsCustomLabels;
  const readOnly = Boolean(isLabelRequired && associationLabelDefinition.label || disabled);
  const tooltip = useMemo(() => getAssociationLabelInputTooltip({
    hasSpecificLabelRequired: isLabelRequired,
    isUnsupportedForLabels,
    label: associationLabelDefinition.label
  }), [isLabelRequired, isUnsupportedForLabels, associationLabelDefinition.label]);
  return /*#__PURE__*/_jsx(FormControl, {
    label: unescapedText('customerDataProperties.AssociationLabelInput.associationLabel'),
    marginBlockStart: "0",
    required: isLabelRequired,
    tooltip: tooltip,
    error: error,
    validationMessage: validationMessage,
    children: /*#__PURE__*/_jsx(Select, {
      "data-test-id": `association-label-input-${id}`,
      onChange: event => onChange(event, recordIds || []),
      options: labelOptions,
      multi: false,
      readOnly: readOnly,
      value: value,
      placeholder: placeholder,
      dropdownFooter: !isUnsupportedForLabels && getCreateNewLabelLink({
        fromObjectTypeId: objectTypeId,
        toObjectTypeId,
        hasCustomAssociationLabelsScope,
        hasCustomAssociationLabelsWriteScope
      }),
      clearable: true
    })
  });
};
export default AssociationLabelInput;